import { CairoCustomEnum, Call } from 'starknet';
import { PoolPairToken } from '../../interfaces';
import { Decimal } from '../../datastructures';
import { MULTICALL_CONTRACT_ADDRESS, POOL_TOKEN_TO_PRAGMA_PAIR_ID_MAP, PRAGMA_ORACLE_ADDRESS } from '../../constants';
import { MultiCallContract } from '../../services';
import { getConfigManager } from '../../config/getConfigManager';
import storage from '../../storage';
import { createAsyncAtom } from '../../lib/state';
import rpcProviderAtom from '../rpcProviderAtom';

const RESPONSE_CHUNK_SIZE = 7;

const poolTokenRatesAtom = createAsyncAtom<{ [tokenAddress: string]: Decimal }>(
  async get => {
    const provider = await get(rpcProviderAtom);

    const pools = getConfigManager().getConfig().pools;

    const pairTokens: PoolPairToken[] = [];
    Object.entries(pools).forEach(async ([, pool]) => {
      const existingToken = pairTokens.find(pairToken => pairToken.address === pool.pair.tokenA.address);
      if (!existingToken) {
        pairTokens.push(pool.pair.tokenA);
      }

      const existingTokenB = pairTokens.find(pairToken => pairToken.address === pool.pair.tokenB.address);
      if (!existingTokenB) {
        pairTokens.push(pool.pair.tokenB);
      }
    });

    const tokenRatesMap: { [tokenAddress: string]: Decimal } = {};

    const calldata: (Call & { userData: { tokenAddress: string } })[] = pairTokens
      .filter(pairToken => {
        return (
          pairToken.ticker !== 'STRONK' &&
          pairToken.ticker !== 'BRRR' &&
          pairToken.ticker !== 'TONY' &&
          pairToken.ticker !== 'AKU' &&
          pairToken.ticker !== 'UNO' &&
          pairToken.ticker !== 'PAL' &&
          pairToken.ticker !== 'zUSDC'
        );
      })
      .map(pairToken => {
        // We want to use STRK price for nstSTRK
        if (pairToken.ticker === 'nstSTRK') {
          return {
            contractAddress: PRAGMA_ORACLE_ADDRESS,
            entrypoint: 'get_data_median',
            calldata: [new CairoCustomEnum({ SpotEntry: POOL_TOKEN_TO_PRAGMA_PAIR_ID_MAP.get('STRK') })],
            userData: {
              tokenAddress: pairToken.address,
            },
          };
        }

        return {
          contractAddress: PRAGMA_ORACLE_ADDRESS,
          entrypoint: 'get_data_median',
          calldata: [new CairoCustomEnum({ SpotEntry: POOL_TOKEN_TO_PRAGMA_PAIR_ID_MAP.get(pairToken.ticker) })],
          userData: {
            tokenAddress: pairToken.address,
          },
        };
      });

    const multiCallContract = new MultiCallContract(MULTICALL_CONTRACT_ADDRESS, provider);

    const result = await multiCallContract.aggregate(calldata);
    if (!result) {
      return {};
    }

    for (let i = 0; i < result.length; i += RESPONSE_CHUNK_SIZE) {
      const pairTokenAddress = calldata[i / RESPONSE_CHUNK_SIZE].userData.tokenAddress;

      // Index 2 is rate decimals
      const rateDecimals = Number(result[i + 2]);

      // Index 1 is token rate
      const tokenRate = new Decimal(result[i + 1], rateDecimals);

      tokenRatesMap[pairTokenAddress] = tokenRate;
    }

    const ethPrice = tokenRatesMap['0x049d36570d4e46f48e99674bd3fcc84644ddd6b96f7c741b1562b82f9e004dc7'];
    const strkPrice = tokenRatesMap['0x04718f5a0fc34cc1af16a1cdee98ffb20c31f5cd61d6ab07201858f4287c938d'];
    const usdcPrice = tokenRatesMap['0x053c91253bc9682c04929ca02ed00b3e423f6710d2ee7e0d5ebb06f3ecf368a8'];

    const [
      poolEthBrrrPrice,
      poolStronkStrkPrice,
      poolStrkTonyPrice,
      poolAkuStrkPrice,
      poolNstStrkStrkPrice,
      poolStrkPalPrice,
      poolzUSDCUSDCPrice,
    ] = await Promise.all([
      storage.getPoolLastPrice('0x033c4141c8eb6ab8e7506c6f09c1a64b0995c9a5fa2ba6fa827845535b942786', 18, 18),
      storage.getPoolLastPrice('0x013e7962df51aba2afedbc1c86b0b61d36410f97fc75cb8f51e525559bef49f6', 18, 18),
      storage.getPoolLastPrice('0x0344653508c3b8831d6826712004f5bcff9d7a9a8fe720ba8e8b6005fb23c04d', 18, 18),
      storage.getPoolLastPrice('0x05737b6463e8aab45d9237180ac68515a49fa3e0656f06b5831c15c69af83332', 18, 18),
      storage.getPoolLastPrice('0x076def79cc9a3a375779c163ad12996f99fbeb4acd68d7041529159bde897160', 18, 18),
      storage.getPoolLastPrice('0x07d24fc0949e9579cb6e08bb65ffe39fd5dd78a47ad2e4eb52e49b97c2cd26db', 18, 18),
      storage.getPoolLastPrice('0x5458b28f32b5f6e635895063ec0fe85c5a3864d257c4ae293edd5f66acf988d', 6, 6),
    ]);

    // BRRR price = ETH price * ETH amount / BRRR amount = ETH price * (1 / ETH-BRRR pool price)
    const brrrPrice = new Decimal(1).div(poolEthBrrrPrice).mul(ethPrice);
    // STRONK price = STRK price * STRK amount / STRONK amount = STRK price * STRONK-STRK pool price
    const stronkPrice = poolStronkStrkPrice.mul(strkPrice);
    // TONY price = STRK price * STRK amount / TONY amount = STRK price * (1 / STRK-TONY pool price)
    const tonyPrice = new Decimal(1).div(poolStrkTonyPrice).mul(strkPrice);
    // AKU price = STRK price * STRK amount / AKU amount = STRK price * AKU-STRK pool price
    const akuPrice = poolAkuStrkPrice.mul(strkPrice);
    // nstSTRK price = STRK price * STRK amount / nstSTRK amount = STRK price * nstSTRK-STRK pool price
    const nstStrkPrice = poolNstStrkStrkPrice.mul(strkPrice);
    const unoPrice = usdcPrice; // TODO - Use UNO price from indexer
    // PAL price = STRK price * STRK amount / PAL amount = STRK price * (1 / STRK-PAL pool price)
    const palPrice = new Decimal(1).div(poolStrkPalPrice).mul(strkPrice);

    let zUSDCPrice = new Decimal(1).div(poolzUSDCUSDCPrice).mul(usdcPrice);
    if (poolzUSDCUSDCPrice.equals(0)) {
      zUSDCPrice = usdcPrice;
    }

    tokenRatesMap['0x02b2ba5da3a25d7e0d45487649a9c4535f29711b57b9054099737e7c9f7dc10a'] = stronkPrice;
    tokenRatesMap['0x067737cbee9be3e3042ca86ef41598af7cc36fa6e109193c165b854de07d1df7'] = brrrPrice;
    tokenRatesMap['0x05f467ace847d1cbc6d1efea978752a8b4549fec043286fec1289d19b8c57e67'] = tonyPrice;
    tokenRatesMap['0x0137dfca7d96cdd526d13a63176454f35c691f55837497448fad352643cfe4d4'] = akuPrice;
    tokenRatesMap['0x04619e9ce4109590219c5263787050726be63382148538f3f936c22aa87d2fc2'] = nstStrkPrice;
    tokenRatesMap['0x0719b5092403233201aa822ce928bd4b551d0cdb071a724edd7dc5e5f57b7f34'] = unoPrice;
    tokenRatesMap['0x049201f03a0f0a9e70e28dcd74cbf44931174dbe3cc4b2ff488898339959e559'] = palPrice;
    tokenRatesMap['0x028757d11c97078dd182023b1cc7b9e7659716c631adf94d24f1fa7dc5943072'] = zUSDCPrice;

    /*
    const stakePriceResult = await multiCallContract.aggregate([
      {
        contractAddress: '0x076c4b7bb1ce744e4aae2278724adedd4906ab89998623fe1715877ecb583bde',
        entrypoint: 'pps',
        calldata: [],
      },
    ]);

    if (stakePriceResult) {
      const sSTRKPrice = new Decimal(stakePriceResult[1], Decimal.PRECISION).mul(strkPrice);
      tokenRatesMap['0x0356f304b154d29d2a8fe22f1cb9107a9b564a733cf6b4cc47fd121ac1af90c9'] = sSTRKPrice;
    }
    */

    return tokenRatesMap;
  },
  60000,
  ['user-refresh'],
);

export default poolTokenRatesAtom;
